// src/components/ItemPicker.js — Item List picker for invoice lines
// [marker: item-list v1]
// <ItemSelect>       dropdown grouped by event, with "+ Add new item to the list…"
// <QuickAddItemModal> create an Item List entry without leaving the invoice
import React, { useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { AccountSelect } from './AccountSelect'; // [marker: coa v1]

export const NEW_ITEM_VALUE = '__new__';

const money = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function groupItems(items) {
  const general = [];
  const byEvent = new Map();
  for (const it of items || []) {
    if (!it.event_id) { general.push(it); continue; }
    if (!byEvent.has(it.event_id)) {
      byEvent.set(it.event_id, { label: `${it.event_name || 'Event'}${it.event_date ? ' · ' + fmtDay(it.event_date) : ''}`, items: [] });
    }
    byEvent.get(it.event_id).items.push(it);
  }
  return { general, events: Array.from(byEvent.values()) };
}

function optionLabel(it, inEventGroup) {
  // Inside an event group, drop the " — Event name" suffix the item name carries
  let name = it.name || '';
  if (inEventGroup && it.event_name && name.endsWith(' — ' + it.event_name)) {
    name = name.slice(0, name.length - (' — ' + it.event_name).length);
  }
  const kind = it.source === 'ticket' ? 'Ticket: ' : it.source === 'sponsorship' ? 'Sponsorship: ' : '';
  return `${kind}${name} (${money(it.default_price)})${it.is_active === false ? ' — inactive' : ''}`; // [marker: invoice-links v1]
}

export function ItemSelect({ items, value, onChange, disabled }) {
  const { general, events } = groupItems(items);
  return (
    <select className="form-select" value={value || ''} disabled={disabled}
            onChange={e => onChange(e.target.value)}>
      <option value="">— Custom (type it in) —</option>
      {general.length > 0 && (
        <optgroup label="Items">
          {general.map(it => <option key={it.id} value={it.id}>{optionLabel(it, false)}</option>)}
        </optgroup>
      )}
      {events.map(g => (
        <optgroup key={g.label} label={g.label}>
          {g.items.map(it => <option key={it.id} value={it.id}>{optionLabel(it, true)}</option>)}
        </optgroup>
      ))}
      <option value={NEW_ITEM_VALUE}>+ Add new item to the list…</option>
    </select>
  );
}

export function QuickAddItemModal({ initial, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    defaultPrice: initial?.unitPrice != null && initial.unitPrice !== '' ? String(initial.unitPrice) : '',
    category: '',
    incomeAccountId: null,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Item name is required.');
    if (form.defaultPrice === '' || isNaN(parseFloat(form.defaultPrice))) return toast.error('Enter a price (0 is fine).');
    setSaving(true);
    try {
      const res = await api.post('/invoice-items', {
        name: form.name.trim(),
        description: form.description.trim() || null,
        defaultPrice: parseFloat(form.defaultPrice),
        category: form.category.trim() || null,
        incomeAccountId: form.incomeAccountId || null,
      });
      toast.success('Added to the Item List.');
      onCreated({ ...res.data, source: 'item' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add the item.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">New Item</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Price *</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.defaultPrice} onChange={e => set('defaultPrice', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Category</label>
              <input className="form-input" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Advertising" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Income Account</label>
            <AccountSelect types={['income', 'other_income']} value={form.incomeAccountId}
                           onChange={v => set('incomeAccountId', v)} placeholder="— Choose (can be set later) —" />
          </div>
          <p className="form-hint" style={{ margin: 0 }}>
            Saved to the Item List so it can be picked on any invoice. Edit or retire it later under Item List.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Add Item'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── What an item is tied to (shown under the item picker) ─────────────────
// [marker: invoice-links v1]
export function itemLinkText(it) {
  if (!it) return '';
  const parts = [];
  if (it.event_name || it.catalog_event_name) parts.push(`Event: ${it.event_name || it.catalog_event_name}`);
  const tier = it.link_tier_name || it.catalog_tier_name || (it.sponsorship_tier_id ? 'sponsorship' : null);
  const tt = it.link_ticket_type_name || it.catalog_ticket_type_name || (it.ticket_type_id ? 'ticket' : null);
  if (it.source === 'sponsorship' || tier) parts.push(`Sponsorship${tier && tier !== 'sponsorship' ? ': ' + tier : ''} → adds a sponsor to the event`);
  else if (it.source === 'ticket' || tt) parts.push(`Ticket${tt && tt !== 'ticket' ? ': ' + tt : ''} → adds tickets when paid`);
  if (it.is_active === false || it.catalog_active === false) parts.push('inactive item');
  return parts.join(' · ');
}

// ─── [marker: fixes-oct21 v1] "Sponsorship for: event / level" on an invoice line ──────────────
// A sponsorship line has to say which event and level it's for — that's what creates the sponsor
// on the event, the member page, the Item List and the reports. Items already linked to a level
// fill it in by themselves; typed-in lines (or unlinked items) ask here.
let _tiers = null; let _tierPromise = null;
export function useSponsorLevels() {
  const [tiers, setTiers] = React.useState(_tiers || []);
  React.useEffect(() => {
    if (!_tierPromise) _tierPromise = api.get('/sponsor-links/tiers').then(r => { _tiers = r.data || []; return _tiers; }).catch(() => []);
    let on = true; _tierPromise.then(t => on && setTiers(t)); return () => { on = false; };
  }, []);
  return tiers;
}
export function itemTierId(item) {
  if (!item) return null;
  return item.sponsorship_tier_id || item.link_tier_id || (item.source === 'sponsorship' ? -1 : null);
}
export function LineSponsorLevel({ line, item, invoiceType, tiers, onChange }) {
  const linkedByItem = itemTierId(item);
  const looksLikeSponsorship = invoiceType === 'sponsorship' || /sponsor/i.test(line.name || '') || /sponsor/i.test(item?.category || '') || !!line.sponsorshipTierId;
  if (linkedByItem || !looksLikeSponsorship) return null;
  const byEvent = new Map();
  tiers.forEach(t => { if (!byEvent.has(t.event_id)) byEvent.set(t.event_id, { label: `${t.event_name}${t.event_date ? ' · ' + fmtDay(t.event_date + 'T12:00:00') : ''}`, items: [] }); byEvent.get(t.event_id).items.push(t); });
  const missing = !line.sponsorshipTierId;
  return (
    <div style={{ marginTop: 4, padding: '4px 6px', borderRadius: 5, background: missing ? '#fffbeb' : '#ecfdf5', border: `1px solid ${missing ? '#fde68a' : '#a7f3d0'}` }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: missing ? '#92400e' : '#065f46', marginBottom: 2 }}>
        {missing ? 'Which event & sponsorship level is this for?' : 'Sponsorship for'}
      </div>
      <select className="form-select" style={{ height: 30, fontSize: '0.8rem', padding: '2px 6px' }} value={line.sponsorshipTierId || ''}
              onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}>
        <option value="">— Not linked (won’t show on an event) —</option>
        {Array.from(byEvent.values()).map(g => (
          <optgroup key={g.label} label={g.label}>
            {g.items.map(t => <option key={t.id} value={t.id}>{t.name} ({money(t.price)})</option>)}
          </optgroup>
        ))}
      </select>
    </div>
  );
}
